// Shared markdown components configuration for consistent formatting across the app

export const getMarkdownComponents = (colors = { primary: '#1890ff', text: '#333', headings: '#333' }) => ({
  // Headers
  h1: ({children}) => (
    <h1 style={{
      fontSize: '1.5em',
      fontWeight: 'bold',
      marginTop: '1em',
      marginBottom: '0.5em',
      color: colors.headings || '#333',
      borderBottom: '2px solid rgba(255,255,255,0.08)',
      paddingBottom: '0.3em'
    }}>{children}</h1>
  ),
  h2: ({children}) => (
    <h2 style={{
      fontSize: '1.3em',
      fontWeight: 'bold',
      marginTop: '1em',
      marginBottom: '0.5em',
      color: colors.headings || '#333'
    }}>{children}</h2>
  ),
  h3: ({children}) => (
    <h3 style={{
      fontSize: '1.1em',
      fontWeight: 'bold',
      marginTop: '0.8em',
      marginBottom: '0.4em',
      color: colors.headings || '#333'
    }}>{children}</h3>
  ),
  h4: ({children}) => (
    <h4 style={{
      fontSize: '1em',
      fontWeight: 'bold',
      marginTop: '0.8em',
      marginBottom: '0.4em',
      color: colors.headings || '#333'
    }}>{children}</h4>
  ),
  // Paragraphs
  p: ({children}) => (
    <p style={{
      marginBottom: '1em',
      lineHeight: '1.6',
      color: colors.text || '#333'
    }}>{children}</p>
  ),
  // Strong/Bold text
  strong: ({children}) => (
    <strong style={{
      fontWeight: 'bold',
      color: colors.headings || '#2c3e50'
    }}>{children}</strong>
  ),
  // Lists
  ul: ({children}) => (
    <ul style={{
      marginLeft: '0',
      marginBottom: '1em',
      paddingLeft: '1.5em',
      listStyleType: 'disc'
    }}>{children}</ul>
  ),
  ol: ({children}) => (
    <ol style={{
      marginLeft: '0',
      marginBottom: '1em',
      paddingLeft: '1.5em',
      listStyleType: 'decimal'
    }}>{children}</ol>
  ),
  li: ({children}) => (
    <li style={{
      marginBottom: '0.5em',
      lineHeight: '1.6',
      color: colors.text || '#333',
      display: 'list-item'
    }}>{children}</li>
  ),
  // Blockquotes
  blockquote: ({children}) => (
    <blockquote style={{
      borderLeft: '4px solid #ddd',
      paddingLeft: '1em',
      margin: '1em 0',
      fontStyle: 'italic',
      color: '#666',
      background: '#f9f9f9',
      padding: '0.5em 1em',
      borderRadius: '4px'
    }}>{children}</blockquote>
  ),
  // Tables with error handling
  table: ({children}) => {
    try {
      return (
        <div style={{ overflowX: 'auto', margin: '16px 0' }}>
          <table style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '13px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden',
          }}>{children}</table>
        </div>
      );
    } catch (error) {
      return <div style={{ padding: '1em', background: '#f5f5f5', borderRadius: '4px' }}>Table content</div>;
    }
  },
  th: ({children}) => {
    try {
      return (
        <th style={{
          border: 'none',
          borderBottom: '2px solid #e2e8f0',
          padding: '10px 14px',
          background: '#f8fafc',
          fontWeight: 600,
          textAlign: 'left',
          color: '#374151',
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>{children}</th>
      );
    } catch (error) {
      return <span style={{ fontWeight: 'bold' }}>{children}</span>;
    }
  },
  td: ({children}) => {
    try {
      return (
        <td style={{
          border: 'none',
          borderBottom: '1px solid #f1f5f9',
          padding: '10px 14px',
          color: '#374151',
          verticalAlign: 'top',
        }}>{children}</td>
      );
    } catch (error) {
      return <span>{children}</span>;
    }
  },
  // Add table body and row handlers
  tbody: ({children}) => <tbody>{children}</tbody>,
  thead: ({children}) => <thead>{children}</thead>,
  tr: ({children, ...props}) => (
    <tr style={{ transition: 'background 0.1s' }} {...props}>{children}</tr>
  ),
  // Links
  a: ({href, children}) => (
    <a href={href} style={{
      color: colors.primary,
      textDecoration: 'underline'
    }} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  // Horizontal rule
  hr: () => (
    <hr style={{
      border: 'none',
      borderTop: '1px solid #eee',
      margin: '1.5em 0'
    }} />
  ),
  // Code blocks and inline code
  code({node, inline, className, children, ...props}) {
    return inline ? (
      <code className={className} {...props} style={{
        background: '#f5f5f5',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.9em',
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        color: '#d63384',
        border: '1px solid #e9ecef'
      }}>
        {children}
      </code>
    ) : (
      <pre style={{
        background: '#f8f9fa',
        padding: '16px',
        borderRadius: '8px',
        overflow: 'auto',
        fontSize: '0.9em',
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        border: '1px solid #e9ecef',
        margin: '1em 0'
      }}>
        <code className={className} {...props} style={{
          background: 'transparent',
          padding: 0,
          color: '#495057'
        }}>
          {children}
        </code>
      </pre>
    );
  }
});